import { CustomerMonthlyReport } from './taskReportService';
import { getCredentialsByType } from './credentialsService';
import { OpenAICredentialData } from '../types/credentials';

/**
 * AI-alapú tesztelői report generálás OpenAI API-val
 */
export async function generateAITestReport(
  report: CustomerMonthlyReport,
  organizationId: string
): Promise<string> {
  // OpenAI credential lekérése
  const credentials = await getCredentialsByType(organizationId, 'openai');
  
  if (credentials.length === 0) {
    throw new Error('Nincs beállított OpenAI credential. Kérlek, add hozzá a Beállítások > Credentials menüpontban.');
  }

  const credential = credentials[0];
  const credentialData = credential.data as OpenAICredentialData;

  // Task-ok összefoglalása az AI számára
  const tasksContext = report.tasks.map((task, index) => {
    return `${index + 1}. ${task.taskNumber} - ${task.title}
   Státusz: ${task.status}
   Prioritás: ${task.priority}
   Felelős: ${task.assignedToName || 'Nincs'}
   Becsült idő: ${task.estimatedHours?.toFixed(1) || '0.0'} óra
   Tényleges idő: ${task.actualHours.toFixed(1)} óra
   Határidő: ${task.dueDate ? task.dueDate.toLocaleDateString('hu-HU') : 'Nincs'}
   Befejezve: ${task.completedDate ? task.completedDate.toLocaleDateString('hu-HU') : 'Még nem'}`;
  }).join('\n\n');

  const prompt = `Készíts egy részletes, professzionális tesztelői reportot az alábbi feladatokról.

ÜGYFÉL: ${report.customerName}
IDŐSZAK: ${report.month}

ÖSSZESÍTÉS:
- Összes feladat: ${report.totalTasksCount} db
- Befejezett feladatok: ${report.completedTasksCount} db
- Becsült óraszám: ${report.totalEstimatedHours.toFixed(1)} óra
- Tényleges óraszám: ${report.totalActualHours.toFixed(1)} óra
- Eltérés: ${(report.totalActualHours - report.totalEstimatedHours).toFixed(1)} óra

FELADATOK:
${tasksContext}

KÖVETELMÉNYEK:
1. Írj egy rövid vezetői összefoglalót (2-3 mondat) a hónap munkájáról
2. Csoportosítsd a feladatokat kategóriák szerint (pl. fejlesztés, hibajavítás, tesztelés)
3. Emeld ki a legfontosabb eredményeket és mérföldköveket
4. Elemezd az időbecslések pontosságát
5. Ha van késés vagy túllépés, adj rá magyarázatot/javaslatot
6. Adj összefoglaló értékelést a hónap teljesítményéről
7. Javasolj következő lépéseket vagy fejlesztési területeket

A report legyen strukturált, professzionális, de könnyen olvasható. Használj magyar nyelvet.
Formázd markdown-ban, használj címsorokat (##), felsorolásokat és félkövér kiemeléseket.`;

  try {
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${credentialData.apiKey}`
      },
      body: JSON.stringify({
        model: credentialData.defaultModel || 'gpt-4o-mini',
        messages: [
          {
            role: 'system',
            content: 'Te egy tapasztalt projekt menedzser és tesztelő vagy, aki részletes, professzionális reportokat készít.'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: 0.7,
        max_tokens: 2000
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`OpenAI API hiba: ${errorData.error?.message || response.statusText}`);
    }

    const data = await response.json();
    return data.choices[0].message.content;
  } catch (error: any) {
    console.error('AI Report Generation Error:', error);
    throw new Error(`AI report generálási hiba: ${error.message}`);
  }
}

/**
 * AI-alapú rövid összefoglaló generálás (gyorsabb, rövidebb)
 */
export async function generateAIQuickSummary(
  report: CustomerMonthlyReport,
  organizationId: string
): Promise<string> {
  const credentials = await getCredentialsByType(organizationId, 'openai');
  
  if (credentials.length === 0) {
    throw new Error('Nincs beállított OpenAI credential.');
  }

  const credential = credentials[0];
  const credentialData = credential.data as OpenAICredentialData;

  const prompt = `Készíts egy rövid (3-5 mondatos) vezetői összefoglalót az alábbi havi munkáról:

Ügyfél: ${report.customerName}
Időszak: ${report.month}
Feladatok: ${report.totalTasksCount} db (${report.completedTasksCount} befejezett)
Óraszám: ${report.totalActualHours.toFixed(1)} óra (becsült: ${report.totalEstimatedHours.toFixed(1)} óra)

Emeld ki a legfontosabb eredményeket és ha van, a problémákat. Legyen tömör, lényegre törő.`;

  try {
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${credentialData.apiKey}`
      },
      body: JSON.stringify({
        model: credentialData.defaultModel || 'gpt-4o-mini',
        messages: [
          {
            role: 'system',
            content: 'Te egy tapasztalt projekt menedzser vagy.'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: 0.7,
        max_tokens: 300
      })
    });

    if (!response.ok) {
      throw new Error('OpenAI API hiba');
    }

    const data = await response.json();
    return data.choices[0].message.content;
  } catch (error: any) {
    throw new Error(`AI összefoglaló generálási hiba: ${error.message}`);
  }
}
